import React, { useContext, useEffect, useState } from "react";
import {
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  useColorScheme,
  Dimensions,
  Platform,
} from "react-native";
import { Text, View } from "../../../components/shared/Themed";
import oktaConfig from "../../../configs/okta";
import ContentTitleHeader from "../../../components/core/ContentTitleHeader";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import CompanyLogo from "../../../components/core/CompanyLogo";
import TabBarIcon from "../../../components/core/TabBarIcon";
import { AntDesign } from "@expo/vector-icons";
import {
  addOktaBypassUser,
  getBypassUserDetailsById,
  updateOktaBypassUser,
} from "../../../services/usersService";
import { useToast } from "react-native-toast-notifications";
import { Picker } from "react-native";
import { getAllCompaniesList } from "../../../services/companyService";
import { companyListModel, companyModel } from "../../../models/companyModel";
import { ScrollView } from "react-native-gesture-handler";

type Props = {
  navigation: any;
  route: any;
};

type ValidationErrors = {
  [key: string]: string;
};

const { width, height } = Dimensions.get("window");

const UpdateOktaBypassUserAccountForm: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const colorScheme = useColorScheme();
  const { userId } = route.params;
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [email, setEmail] = useState("");
  const [zone, setZone] = useState("Australia/Sydney");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [team, setTeam] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [role, setRole] = useState("");
  const [login, setLogin] = useState("");
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const auto_sync = false;

  // Fetch user details
  useEffect(() => {
    if (userId === undefined) {
      setTimeout(() => {
        navigation.navigate("OrgSettings", { screen: "Users" });
      }, 1500);
    } else {
      getBypassUserDetailsById(userId)
        .then((data) => setSelectedUser(data))
        .catch((error) => toast.show(error.message, { type: "danger" }));
    }
  }, [userId]);

  // Update state when selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      setEmail(selectedUser.email || "");
      setZone(selectedUser.zone_info || "Australia/Sydney");
      setFirstName(selectedUser.first_name || "");
      setLastName(selectedUser.last_name || "");
      setPassword(selectedUser.password || "");
      setCompanyId(selectedUser?.roles?.[0]?.company_id || "");
      setTeam(selectedUser.team || "");
      setRole(selectedUser?.roles?.[0]?.role || "");
      setLogin(selectedUser.login || "");
    }
  }, [selectedUser]);

  // Fetch company list and update companyName if selectedUser is available
  useEffect(() => {
    const fetchCompanyList = async () => {
      setLoading(true);
      try {
        const data = await getAllCompaniesList();
        setCompanyList(data.company);
        setLoading(false);

        if (selectedUser) {
          const selectedCompany = data.company.find(
            (company: companyModel) =>
              company.company_id === selectedUser?.roles?.[0]?.company_id
          );
          setCompanyName(selectedCompany ? selectedCompany.company_name : "");
        }
      } catch (error: any) {
        setLoading(false);
        toast.show(error.message, { type: "danger" });
      }
    };

    fetchCompanyList();
  }, [selectedUser]);

  const validateFields = () => {
    const validationErrors: { [key: string]: string } = {};
    if (!email.trim()) {
      validationErrors.email = "Email is required.";
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)
    ) {
      validationErrors.email = "Email is not valid.";
    }
    if (!zone.trim()) validationErrors.zone = "Zone is required.";
    if (!login.trim()) {
      validationErrors.login = "Username is required.";
    } else if (login.length > 25) {
      validationErrors.login = "Username must not exceed 25 characters.";
    }
    if (!role.trim()) validationErrors.role = "Role is required.";
    if (!firstName.trim())
      validationErrors.firstName = "First name is required.";
    if (!lastName.trim()) validationErrors.lastName = "Last name is required.";
    if (!team.trim()) validationErrors.team = "Team is required.";
    if (!companyId.trim())
      validationErrors.companyId = "Company Id is required.";

    if (password && password != "") {
      if (password.length > 25) {
        validationErrors.password = "Password must not exceed 25 characters.";
      }
    }
    return validationErrors;
  };

  const submitHandler = async () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const displayName = `${firstName} ${lastName}`;
    const formData = {
      email,
      zone,
      companyId,
      role,
      firstName,
      lastName,
      displayName,
      login,
      team,
      password,
      auto_sync,
    };

    await updateOktaBypassUser(formData, selectedUser?._id)
      .then((result) => {
        toast.show("User updated successfully", {
          type: "success",
        });
        navigation.reset({
          index: 0,
          routes: [{ name: "OrgSettings", params: { screen: "Users" } }],
        });
      })
      .catch((err) => {
        toast.show(
          err.message || "An error occurred while updating this user",
          {
            type: "danger",
          }
        );
      });
  };

  const goBack = () =>
    navigation.reset({
      index: 0,
      routes: [{ name: "OrgSettings", params: { screen: "Users" } }],
    });

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollContent,
        isSmallDevice && { paddingHorizontal: 14 },
      ]}
      showsVerticalScrollIndicator={false}
    >
      {/* Desktop Header */}
      {!isSmallDevice && (
        <View style={{ flexDirection: "row", width: "100%", height: 60, backgroundColor: "#ffffff" }}>
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              width: "40%",
              height: 60,
            }}
          >
            <CompanyLogo />
          </View>
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              width: "60%",
              height: 60,
            }}
          >
            <TouchableOpacity
              onPress={() => navigation.navigate("Root")}
            >
              <TabBarIcon name="home" color={"black"} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Page Header */}
      <View style={[styles.pageHeader, isSmallDevice && { paddingHorizontal: 14, marginHorizontal: -14 }]}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <TouchableOpacity onPress={goBack} style={styles.backButton}>
            <AntDesign name="arrowleft" size={20} color="#374151" />
          </TouchableOpacity>
          <View style={{ flex: 1 }}>
            <Text style={[styles.pageTitle, isSmallDevice && { fontSize: 20 }]}>
              Update User
            </Text>
            <Text style={[styles.pageSubtitle, isSmallDevice && { fontSize: 12 }]}>
              Update user account details
            </Text>
          </View>
        </View>
      </View>

      {/* Form Card */}
      <View style={[styles.card, isSmallDevice && { paddingHorizontal: 16 }]}>
        {/* Email */}
        <Text style={styles.label}>Email</Text>
        <TextInput
          style={[styles.input, errors.email && styles.inputError]}
          onChangeText={setEmail}
          value={email}
          placeholder="Enter email"
          placeholderTextColor="#9CA3AF"
          keyboardType="email-address"
          autoCapitalize="none"
        />
        {errors.email && <Text style={styles.errorText}>{errors.email}</Text>}

        {/* Zone */}
        <Text style={styles.label}>Zone</Text>
        <View style={styles.pickerContainer}>
          <Picker
            selectedValue={zone}
            onValueChange={(value: string) => setZone(value)}
            style={styles.picker}
          >
            <Picker.Item label="Select a zone" value="" />
            <Picker.Item label="Australia/Brisbane" value="Australia/Brisbane" />
            <Picker.Item label="Australia/Melbourne" value="Australia/Melbourne" />
            <Picker.Item label="Australia/Sydney" value="Australia/Sydney" />
            <Picker.Item label="Australia/Tasmania" value="Australia/Tasmania" />
            <Picker.Item label="Australia/Perth" value="Australia/Perth" />
          </Picker>
        </View>
        {errors.zone && <Text style={styles.errorText}>{errors.zone}</Text>}

        {/* First & Last Name */}
        <View style={[styles.row, isSmallDevice && { flexDirection: "column" }]}>
          <View style={[styles.halfField, isSmallDevice && { width: "100%" }]}>
            <Text style={styles.label}>First Name</Text>
            <TextInput
              style={[styles.input, errors.firstName && styles.inputError]}
              onChangeText={setFirstName}
              value={firstName}
              placeholder="First name"
              placeholderTextColor="#9CA3AF"
            />
            {errors.firstName && (
              <Text style={styles.errorText}>{errors.firstName}</Text>
            )}
          </View>
          <View style={[styles.halfField, isSmallDevice && { width: "100%", marginLeft: 0 }]}>
            <Text style={styles.label}>Last Name</Text>
            <TextInput
              style={[styles.input, errors.lastName && styles.inputError]}
              onChangeText={setLastName}
              value={lastName}
              placeholder="Last name"
              placeholderTextColor="#9CA3AF"
            />
            {errors.lastName && (
              <Text style={styles.errorText}>{errors.lastName}</Text>
            )}
          </View>
        </View>

        {/* Username */}
        <Text style={styles.label}>Username</Text>
        <TextInput
          style={[styles.input, errors.login && styles.inputError]}
          onChangeText={setLogin}
          value={login}
          placeholder="Enter username"
          placeholderTextColor="#9CA3AF"
          autoCapitalize="none"
        />
        {errors.login && <Text style={styles.errorText}>{errors.login}</Text>}

        {/* Company */}
        <Text style={styles.label}>Company</Text>
        <View style={styles.pickerContainer}>
          <Picker
            selectedValue={companyId}
            onValueChange={(value: any) => {
              const selectedCompany = companyList.find(
                (company: companyListModel) => company.company_id === value
              );
              setCompanyId(value);
              setCompanyName(
                selectedCompany ? selectedCompany?.company_name : ""
              );
            }}
            style={styles.picker}
          >
            <Picker.Item label="Select a company" value="" />
            {companyList.map((company: companyListModel) => (
              <Picker.Item
                key={company.company_id}
                label={company.company_name}
                value={company.company_id}
              />
            ))}
          </Picker>
        </View>
        {errors.companyId && (
          <Text style={styles.errorText}>{errors.companyId}</Text>
        )}

        {/* Team & Role */}
        <View style={[styles.row, isSmallDevice && { flexDirection: "column" }]}>
          <View style={[styles.halfField, isSmallDevice && { width: "100%" }]}>
            <Text style={styles.label}>Team</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={team}
                onValueChange={(value: string) => setTeam(value)}
                style={styles.picker}
              >
                <Picker.Item label="Select a team" value="" />
                <Picker.Item label="Operation" value="operation" />
                <Picker.Item label="Sales" value="sales" />
                <Picker.Item label="Sales Manager" value="sales_manager" />
                <Picker.Item label="IT" value="it" />
                <Picker.Item label="Warehouse" value="warehouse" />
                <Picker.Item label="Purchasing" value="purchasing" />
                <Picker.Item label="China" value="china" />
              </Picker>
            </View>
            {errors.team && <Text style={styles.errorText}>{errors.team}</Text>}
          </View>
          <View style={[styles.halfField, isSmallDevice && { width: "100%", marginLeft: 0 }]}>
            <Text style={styles.label}>Role</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={role}
                onValueChange={(value: string) => setRole(value)}
                style={styles.picker}
              >
                <Picker.Item label="Select a role" value="" />
                <Picker.Item label="MEMBER" value="MEMBER" />
                <Picker.Item label="OWNER" value="OWNER" />
                <Picker.Item label="ADMIN" value="ADMIN" />
              </Picker>
            </View>
            {errors.role && <Text style={styles.errorText}>{errors.role}</Text>}
          </View>
        </View>

        {/* Password */}
        <Text style={styles.label}>Password (Leave blank to keep unchanged)</Text>
        <View style={styles.passwordContainer}>
          <TextInput
            style={[styles.passwordInput, errors.password && styles.inputError]}
            onChangeText={setPassword}
            value={password}
            placeholder="Enter new password"
            placeholderTextColor="#9CA3AF"
            secureTextEntry={!showPassword}
          />
          <TouchableOpacity
            style={styles.eyeButton}
            onPress={() => setShowPassword(!showPassword)}
          >
            <AntDesign
              name={showPassword ? "eye" : "eyeo"}
              size={20}
              color="#9CA3AF"
            />
          </TouchableOpacity>
        </View>
        {errors.password && (
          <Text style={styles.errorText}>{errors.password}</Text>
        )}

        {/* Submit */}
        <TouchableOpacity style={styles.submitButton} onPress={submitHandler}>
          <Text style={styles.submitButtonText}>Update User</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingBottom: 48,
  },

  /* Page Header */
  pageHeader: {
    paddingTop: 20,
    paddingBottom: 16,
    marginBottom: 16,
    paddingHorizontal: 24,
    marginHorizontal: -24,
    backgroundColor: "#ffffff",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#111827",
  },
  pageSubtitle: {
    fontSize: 13,
    color: "#6B7280",
    marginTop: 2,
  },

  /* Card */
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    paddingVertical: 20,
    paddingHorizontal: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
    maxWidth: 800,
    width: "100%",
    alignSelf: "center",
  },

  /* Form */
  label: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: "100%",
    height: 44,
    backgroundColor: "#F9FAFB",
    paddingHorizontal: 14,
    borderColor: "#D1D5DB",
    borderWidth: 1,
    borderRadius: 8,
    fontSize: 14,
    color: "#111827",
  },
  inputError: {
    borderColor: "#EF4444",
    backgroundColor: "#FEF2F2",
  },
  passwordContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    height: 44,
  },
  passwordInput: {
    flex: 1,
    height: 44,
    paddingHorizontal: 14,
    fontSize: 14,
    color: "#111827",
  },
  eyeButton: {
    paddingHorizontal: 12,
    justifyContent: "center",
    alignItems: "center",
    height: 44,
  },
  errorText: {
    color: "#EF4444",
    fontSize: 12,
    marginTop: 4,
  },
  row: {
    flexDirection: "row",
    width: "100%",
    gap: 12,
  },
  halfField: {
    flex: 1,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    height: 44,
    justifyContent: "center",
  },
  picker: {
    height: 44,
    backgroundColor: "transparent",
    marginHorizontal: 4,
    borderWidth: 0,
    borderColor: "transparent",
  },

  /* Submit */
  submitButton: {
    alignItems: "center",
    justifyContent: "center",
    height: 46,
    backgroundColor: "#2563EB",
    borderRadius: 10,
    marginTop: 24,
    width: "100%",
    shadowColor: "#2563EB",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 3,
  },
  submitButtonText: {
    color: "#ffffff",
    fontSize: 15,
    fontWeight: "600",
  },
});

export default UpdateOktaBypassUserAccountForm;
